import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '@iconify/react';
import Sidebar from '../../components/Sidebar';
import { Button, LoadingButton } from '../../components/ui/Button';
import { useToast } from '../../components/ui';
import apiClient from '../../services/api/apiClient';

function UserInfoPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    bio: '',
    timezone: 'Asia/Taipei',
    language: 'zh'
  });

  useEffect(() => {
    if (user) {
      setProfileData((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await apiClient.put('/auth/profile', profileData);

      if (response.data.success) {
        toast.success('個人資料已更新');
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      toast.error('更新失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const timezones = [
    { value: 'Asia/Taipei', label: '台北 (GMT+8)' },
    { value: 'Asia/Tokyo', label: '東京 (GMT+9)' },
    { value: 'Asia/Shanghai', label: '上海 (GMT+8)' },
    { value: 'America/New_York', label: '紐約 (GMT-5)' },
    { value: 'America/Los_Angeles', label: '洛杉磯 (GMT-8)' },
    { value: 'Europe/London', label: '倫敦 (GMT+0)' }
  ];

  const languages = [
    { value: 'zh', label: '中文' },
    { value: 'en', label: 'English' }
  ];

  return (
    <div className="flex min-h-screen" style={{ background: theme.bgPrimary }}>
      <Sidebar activePage="" />

      <div className="flex-1 p-10 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 flex items-center gap-3" style={{ color: theme.textPrimary }}>
            <Icon icon="mdi:account-outline" className="text-blue-500" />
            Account
          </h1>
          <p className="text-sm" style={{ color: theme.textSecondary }}>
            管理您的個人資料和偏好設定
          </p>
        </div>

        {/* Profile Card */}
        <div
          className="rounded-xl p-6 mb-6"
          style={{
            background: theme.bgSecondary,
            border: `1px solid ${theme.borderColor}`
          }}
        >


          {/* Form Fields */}
          <div className="grid gap-5">
            {/* Name */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: theme.textPrimary }}
              >
                姓名
              </label>
              <input
                type="text"
                value={profileData.name}
                onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-lg text-sm"
                style={{
                  background: theme.bgPrimary,
                  border: `1px solid ${theme.borderColor}`,
                  color: theme.textPrimary
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: theme.textPrimary }}
              >
                電子郵件
              </label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-3.5 py-2.5 rounded-lg text-sm cursor-not-allowed"
                style={{
                  background: theme.bgTertiary || theme.bgSecondary,
                  border: `1px solid ${theme.borderColor}`,
                  color: theme.textSecondary
                }}
              />
              <p className="text-xs mt-1" style={{ color: theme.textTertiary }}>
                電子郵件無法修改
              </p>
            </div>


            {/* Timezone */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: theme.textPrimary }}
              >
                時區
              </label>
              <select
                value={profileData.timezone}
                onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                className="w-full px-3.5 py-2.5 rounded-lg text-sm cursor-pointer"
                style={{
                  background: theme.bgPrimary,
                  border: `1px solid ${theme.borderColor}`,
                  color: theme.textPrimary
                }}
              >
                {timezones.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 flex gap-3">
            <LoadingButton
              variant="primary"
              size="lg"
              loading={loading}
              loadingText="儲存中..."
              icon="mdi:check"
              onClick={handleSave}
            >
              儲存變更
            </LoadingButton>
            <Button
              variant="secondary"
              size="lg"
              to="/projects"
            >
              取消
            </Button>
          </div>
        </div>

        {/* Danger Zone */}
        <div
          className="rounded-xl p-6"
          style={{
            background: theme.bgSecondary,
            border: '1px solid #ef4444'
          }}
        >
          <h2 className="text-xl font-semibold text-red-500 mb-4">
            危險區域
          </h2>
          <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
            刪除帳號將永久移除所有資料，此操作無法復原
          </p>
          <Button
            variant="danger-outline"
            size="md"
            icon="mdi:delete-outline"
          >
            刪除帳號
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UserInfoPage;
